/**
 * 错误上报配置的取值(可观测性契约 §4)。
 *
 * ★为什么以**运行时注入**为主、构建期 VITE_SENTRY_DSN 只做兜底:
 *   console 是「一份镜像跑三台」—— CI 只在打 tag 时构建一次 `gscq/admin:<tag>`,
 *   同一个镜像先上测试机、再上生产(演示机同理),而且同一份 bundle 还要服务主域、
 *   租户企微域、OEM 渠道商贴牌域名。构建期把 DSN / environment 烧进 bundle,
 *   就没法让同一个镜像在测试机报 `test`、在生产报 `prod`;私有化镜像交到客户机房后
 *   也必须能「不配就不报」。
 *   ⇒ 容器启动时 docker/40-runtime-config.sh 按容器环境变量写 `/runtime-config.json`,
 *     前端在 load 之后异步取一次。取不到(本机 dev、老镜像)再看构建期变量。
 *
 * ★DSN 为空 = 不初始化,和今天行为完全一致。
 */

export interface ErrorReportingConfig {
  dsn: string;
  environment: string;
  release: string;
}

interface RuntimeConfigFile {
  sentryDsn?: unknown;
  sentryEnvironment?: unknown;
  sentryRelease?: unknown;
}

export const RUNTIME_CONFIG_URL = "/runtime-config.json";
const FETCH_TIMEOUT_MS = 3000;

/** Sentry DSN 形状:`https://<key>@<host>/<project>`。不像就当没配,别让 SDK 去猜。 */
export function isValidDsn(dsn: string): boolean {
  return /^https?:\/\/[^@\s/]+@[^/\s]+\/\S+$/.test(dsn);
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** 构建期兜底的 release:CI tag / commit,由 vite.config.ts 的 define 注入。 */
export function buildTimeRelease(): string {
  return typeof __APP_RELEASE__ === "string" ? __APP_RELEASE__ : "";
}

async function fetchRuntimeConfig(fetchImpl: typeof fetch): Promise<RuntimeConfigFile | null> {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : undefined;
  try {
    const res = await fetchImpl(RUNTIME_CONFIG_URL, {
      cache: "no-store",
      credentials: "omit",
      signal: controller?.signal,
    });
    if (!res.ok) return null;
    // nginx 的 SPA 兜底会把不存在的路径回成 index.html(200 + text/html)—— 解析失败即视为没有
    const body = (await res.json()) as unknown;
    return body && typeof body === "object" ? (body as RuntimeConfigFile) : null;
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * 合并运行时 + 构建期配置;没有可用 DSN 返回 null(= 不初始化)。
 * 优先级:运行时非空值 > 构建期 VITE_* > 默认。
 */
export async function resolveErrorReportingConfig(
  fetchImpl: typeof fetch = fetch,
  buildEnv: {
    VITE_SENTRY_DSN?: string;
    VITE_SENTRY_ENVIRONMENT?: string;
  } = import.meta.env
): Promise<ErrorReportingConfig | null> {
  const runtime = await fetchRuntimeConfig(fetchImpl);
  const dsn = str(runtime?.sentryDsn) || str(buildEnv.VITE_SENTRY_DSN);
  if (!dsn || !isValidDsn(dsn)) return null;
  return {
    dsn,
    environment:
      str(runtime?.sentryEnvironment) || str(buildEnv.VITE_SENTRY_ENVIRONMENT) || "unknown",
    release: str(runtime?.sentryRelease) || buildTimeRelease() || "unknown",
  };
}
